/**
 * Template + sheet → the concrete pages of a document, and the effective
 * settings of each viewport on them.
 */

import { PRINT_BLACK } from './color';
import { pageViewports, type PrintPageSpec, type PrintTemplateElement, type PrintTemplateSpec, type ViewportElement } from './spec';
import { defaultViewportSettings, pageInstanceKey, viewportSettingsKey, type SheetDoc, type ViewportSettings } from './sheet';

export interface PrintFloorInfo {
  id: string;
  name: string;
  /** Floor level in world meters; per-floor pages are ordered by it. */
  elevation: number;
}

export interface PageInstance {
  /** `<pageId>` or `<pageId>:<floorId>`. */
  key: string;
  page: PrintPageSpec;
  floor: PrintFloorInfo | null;
  /** 1-based position in the exported document. */
  number: number;
}

/**
 * Document pages in order. A `perFloor` page yields one instance per floor
 * the sheet selects (all floors when `floorIds` is null), lowest first; a
 * per-floor page with no floors yields nothing. Hidden instances are dropped
 * and numbering skips them.
 */
/**
 * A page as a sheet shows it: the template's elements with the sheet's
 * moves, resizes and removals applied, plus the elements the sheet added.
 * A removed view takes its scale bars and north arrows along (they belong to
 * it), as do its legends.
 */
export function sheetPage(page: PrintPageSpec, sheet: Pick<SheetDoc, 'pageElements' | 'elementEdits'>): PrintPageSpec {
  const edits = sheet.elementEdits ?? {};
  const added = sheet.pageElements?.[page.id] ?? [];
  if (Object.keys(edits).length === 0 && added.length === 0) return page;
  const removed = new Set(Object.entries(edits).flatMap(([id, edit]) => (edit.removed ? [id] : [])));
  const place = (element: PrintTemplateElement): PrintTemplateElement[] => {
    if (removed.has(element.id)) return [];
    if ((element.kind === 'scaleBar' || element.kind === 'northArrow' || element.kind === 'legend') && element.viewportId !== null && removed.has(element.viewportId)) return [];
    const edit = edits[element.id];
    let next = edit ? { ...element, xMm: edit.xMm ?? element.xMm, yMm: edit.yMm ?? element.yMm, wMm: edit.wMm ?? element.wMm, hMm: edit.hMm ?? element.hMm } : element;
    if (next.kind === 'viewport' && edit?.outline !== undefined) {
      next = { ...next, frame: edit.outline ? (next.frame ?? { color: PRINT_BLACK, weightMm: 0.25, dashMm: [], cap: 'butt', join: 'miter' }) : null };
    }
    return [next];
  };
  return { ...page, elements: [...page.elements.flatMap(place), ...added.flatMap(place)] };
}

export function expandPages(spec: PrintTemplateSpec, sheet: Pick<SheetDoc, 'floorIds' | 'hiddenPages' | 'pageElements' | 'elementEdits'>, floors: readonly PrintFloorInfo[]): PageInstance[] {
  const selected = (sheet.floorIds === null ? [...floors] : floors.filter((floor) => sheet.floorIds?.includes(floor.id))).sort((a, b) => a.elevation - b.elevation);
  const hidden = new Set(sheet.hiddenPages);
  const out: PageInstance[] = [];
  for (const page of spec.pages) {
    const instances = page.repeat === 'perFloor' ? selected.map((floor) => ({ floor, key: pageInstanceKey(page.id, floor.id) })) : [{ floor: null, key: pageInstanceKey(page.id, null) }];
    for (const { floor, key } of instances) {
      if (hidden.has(key)) continue;
      out.push({ key, page: sheetPage(page, sheet), floor, number: out.length + 1 });
    }
  }
  return out;
}

export interface ResolvedViewport {
  element: ViewportElement;
  /** Where edits to this instance are stored (always the instance-specific key). */
  key: string;
  settings: ViewportSettings;
  /** Never framed: the export warns and the editor prompts to reframe. */
  needsFraming: boolean;
}

/**
 * Effective settings of a viewport on a page instance: the instance's own
 * entry, else the page-wide entry (`<pageId>/<elementId>`, shared by every
 * floor of a per-floor page), else the template default. The floor is bound
 * to the instance's floor when the settings follow the page floor.
 */
export function resolveViewport(sheet: Pick<SheetDoc, 'viewports'>, instance: PageInstance, element: ViewportElement): ResolvedViewport {
  const key = viewportSettingsKey(instance.key, element.id);
  const pageWide = viewportSettingsKey(instance.page.id, element.id);
  const stored = sheet.viewports[key] ?? sheet.viewports[pageWide];
  const base = stored ?? defaultViewportSettings(element.defaultKind, element.defaultScaleDen);
  const settings = instance.floor && base.followPageFloor ? { ...base, floorId: instance.floor.id } : base;
  return { element, key, settings, needsFraming: !stored || stored.capturedAt === null };
}

export function resolvePageViewports(sheet: Pick<SheetDoc, 'viewports'>, instance: PageInstance): ResolvedViewport[] {
  return pageViewports(instance.page).map((element) => resolveViewport(sheet, instance, element));
}
